/**
 * Receivables persistence (FR-RCV-01 to FR-RCV-08).
 *
 * The outstanding figure is **never stored** (FR-RCV-02). It is the sum of the entries,
 * derived on read by `core/receivables.ts`. A stored balance beside the entries that
 * produce it is a second source of truth, and the workbook's own receivables block —
 * eight running arithmetic strings, three of which had drifted out of step with their
 * displayed totals across the month sheets — is what that looks like after a year.
 */

import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import type { DateKey } from '../core/dates';
import type { Paise } from '../core/money';
import {
  buildSeedReceivables,
  slug,
  type Receivable,
  type ReceivableEntry,
} from '../core/receivables';
import { auditDelete, auditUpdate } from './audit';
import { db } from './firebase';
import { trackWrite } from './syncState';

const COLLECTION = 'receivables';

export class ReceivableDataError extends Error {}

function toReceivable(id: string, data: Record<string, unknown>): Receivable {
  return {
    id,
    person: (data.person as string) ?? id,
    note: (data.note as string) ?? null,
    entries: (data.entries as ReceivableEntry[]) ?? [],
    archived: data.archived === true,
    source: (data.source as Receivable['source']) ?? 'entered',
  };
}

export function watchReceivables(onChange: (receivables: Receivable[]) => void): () => void {
  return onSnapshot(
    collection(db, COLLECTION),
    (snapshot) =>
      onChange(
        snapshot.docs
          .map((d) => toReceivable(d.id, d.data()))
          .sort((a, b) => a.person.localeCompare(b.person)),
      ),
    () => onChange([]),
  );
}

/**
 * Seed the eight receivables (FR-RCV-04, FR-RCV-05).
 *
 * Returns the problems `buildSeedReceivables` found rather than throwing on them: a
 * derived total that does not match the sheet is something the user must see, and
 * refusing to seed would leave nothing to compare against.
 */
export async function seedReceivables(by: string): Promise<{ count: number; problems: string[] }> {
  const existing = await getDocs(collection(db, COLLECTION));
  if (!existing.empty) {
    throw new ReceivableDataError(
      `Receivables already exist (${existing.size}). Seeding would overwrite them.`,
    );
  }

  const { receivables, problems } = buildSeedReceivables();

  for (const receivable of receivables) {
    const { id, ...rest } = receivable;
    await trackWrite(
      setDoc(doc(db, COLLECTION, id), {
        ...rest,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        enteredBy: by,
      }),
    );
  }

  return { count: receivables.length, problems };
}

export async function createReceivable(
  input: { person: string; amountPaise: Paise; lentOn: DateKey | null; note?: string | null },
  existing: Receivable[],
  by: string,
): Promise<string> {
  const person = input.person.trim();
  if (!person) throw new ReceivableDataError('A receivable needs a name.');

  const id = slug(person);
  if (!id) throw new ReceivableDataError('That name produces an empty id.');
  if (existing.some((r) => r.id === id)) {
    throw new ReceivableDataError(`"${person}" already has a receivable. Add an entry to it instead.`);
  }

  await trackWrite(
    setDoc(doc(db, COLLECTION, id), {
      person,
      note: input.note ?? null,
      entries: [{ amountPaise: input.amountPaise, dateKey: input.lentOn, note: null }],
      archived: false,
      source: 'entered',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      enteredBy: by,
    }),
  );

  return id;
}

/**
 * Record a lending or a repayment (FR-RCV-02).
 *
 * Appended to the entry list rather than netted into a balance, so "PK repaid 5,000 on
 * 12 June" survives as an event and the outstanding figure stays explicable.
 */
export async function addEntry(
  receivable: Receivable,
  entry: ReceivableEntry,
  by: string,
): Promise<void> {
  if (!Number.isSafeInteger(entry.amountPaise) || entry.amountPaise === 0) {
    throw new ReceivableDataError('An entry needs a non-zero amount.');
  }

  const entries = [...receivable.entries, entry];
  await trackWrite(
    updateDoc(doc(db, COLLECTION, receivable.id), { entries, updatedAt: serverTimestamp() }),
  );
  await auditUpdate(
    COLLECTION,
    receivable.id,
    { entries: receivable.entries.length },
    { entries: entries.length },
    by,
  );
}

/** Date an entry that arrived undated from the workbook (FR-IMP-11). */
export async function dateEntry(
  receivable: Receivable,
  index: number,
  dateKey: DateKey,
  by: string,
): Promise<void> {
  const entries = receivable.entries.map((entry, i) =>
    i === index ? { ...entry, dateKey } : entry,
  );
  if (entries.length !== receivable.entries.length || !entries[index]) {
    throw new ReceivableDataError('That entry no longer exists.');
  }

  await trackWrite(
    updateDoc(doc(db, COLLECTION, receivable.id), { entries, updatedAt: serverTimestamp() }),
  );
  await auditUpdate(
    COLLECTION,
    receivable.id,
    { [`entry${index}`]: receivable.entries[index].dateKey },
    { [`entry${index}`]: dateKey },
    by,
  );
}

export async function setArchived(
  receivable: Receivable,
  archived: boolean,
  by: string,
): Promise<void> {
  await trackWrite(
    updateDoc(doc(db, COLLECTION, receivable.id), { archived, updatedAt: serverTimestamp() }),
  );
  await auditUpdate(
    COLLECTION,
    receivable.id,
    { archived: receivable.archived },
    { archived },
    by,
  );
}

/**
 * Delete a receivable outright (FR-RCV-03).
 *
 * The only hard delete in the app, and it is here because FR-RCV-03 names it explicitly
 * while FR-DEL-01's soft-delete regime is written for transactions. The rules still deny
 * it — `allow delete: if false` on this collection — so this path fails unless the rule
 * is changed, and the audit entry is written first so the record of the deletion survives
 * the record.
 */
export async function deleteReceivable(receivable: Receivable, by: string): Promise<void> {
  await auditDelete(
    COLLECTION,
    receivable.id,
    receivable as unknown as Record<string, unknown>,
    by,
  );
  await trackWrite(deleteDoc(doc(db, COLLECTION, receivable.id)));
}

/** A free-standing note against a receivable, for context the entries cannot carry. */
export async function addNote(receivableId: string, text: string): Promise<void> {
  await trackWrite(
    addDoc(collection(db, COLLECTION, receivableId, 'notes'), {
      text,
      at: serverTimestamp(),
    }),
  );
}
